/**
 * An empty row of a repeatable section, built from the schema that describes it.
 *
 * <p>Hand-writing the blank would mean a list of forty field names in a component, and a field
 * added to the catalog would be missing from every row a reviewer created until someone noticed.
 * Reading the schema also settles the containers: `hospitalization` is a non-nullable object, so a
 * new event that omitted it would be a body the section refuses — the round-trip break phase 08
 * only found by sending a GET response back as a PATCH.
 */
import { NEW_RECORD_ID_PREFIX } from "../constants/caseReviewRecords";

type JsonSchema = Record<string, unknown>;

/** A record id for a row created in this draft, from whatever identifies it uniquely on screen. */
export function newRecordId(...parts: Array<string | number>): string {
  return `${NEW_RECORD_ID_PREFIX}${parts.join("-")}`;
}

/** Whether a record id names a row the server has not created yet. */
export function isNewRecordId(recordId: string): boolean {
  return recordId.startsWith(NEW_RECORD_ID_PREFIX);
}

export function blankRecord(
  itemSchema: JsonSchema,
  recordIdProperty: string,
  recordId: string,
): Record<string, unknown> {
  const properties = itemSchema.properties as Record<string, JsonSchema> | undefined;
  const blank: Record<string, unknown> = {};
  if (!properties) return blank;

  for (const [key, child] of Object.entries(properties)) {
    blank[key] = key === recordIdProperty ? recordId : emptyValue(child, recordIdProperty);
  }
  return blank;
}

/**
 * Nothing, in whatever shape this field allows.
 *
 * <p>Null wherever the schema permits it, because "nobody has said" is the honest state of a field
 * on a row that was created a second ago. Only a container the schema insists on gets built, and
 * it is built empty the same way.
 */
function emptyValue(schema: JsonSchema, recordIdProperty: string): unknown {
  if (allows(schema, "null")) return null;
  if (allows(schema, "array")) return [];
  if (allows(schema, "object")) return blankRecord(schema, recordIdProperty, "");
  return null;
}

/** The catalog emits nullable fields as `["string", "null"]`, so a bare equality misses them. */
function allows(schema: JsonSchema, type: string): boolean {
  const declared = schema.type;
  return Array.isArray(declared) ? declared.includes(type) : declared === type;
}
